import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Spin from "antd/es/spin";
import Button from "antd/es/button";
import CheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import CloseCircleFilled from "@ant-design/icons/CloseCircleFilled";
import ReloadOutlined from "@ant-design/icons/ReloadOutlined";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import "./style.scss";

import useUrlSearchParams from "services/hooks/useUrlSearchParams";
import useAsyncJsonData from "services/hooks/useAsyncJsonData";
import { getSurveyAPI, putSurveyAPI } from "./api";
import { FIELDS_TYPES } from "constants/fieldsTypes";
import notification from "antd/es/notification";
import { RenderableSurveyData, SurveyData, SurveyLanguage } from "./types";
import { SHOW_TIME_FORMAT } from "constants/dateFormats";
import Logo from "components/Logo";
import SurverForm from "./components/SurverForm";
import {
  DEFAULT_SURVEY_LANGUAGE,
  getLocalizedText,
  getQuestionFieldName,
  getVisibleQuestionFieldNames,
  SURVEY_TEXTS,
} from "./utils";

dayjs.extend(isSameOrAfter);

const hasGroupQuestions = (
  groups: SurveyData.Group[] | undefined
): groups is SurveyData.Group[] => {
  if (!Array.isArray(groups)) {
    return false;
  }

  return (
    groups.length > 0 && groups.every((group) => Array.isArray(group.questions))
  );
};

const getRenderableSurveyData = (
  data: SurveyData | null | undefined
): RenderableSurveyData | null => {
  if (!data) {
    return null;
  }

  if (hasGroupQuestions(data.groups)) {
    return { ...data, groups: data.groups };
  }

  if (Array.isArray(data.questions) && data.questions.length > 0) {
    return {
      ...data,
      groups: [
        {
          groupUUID: "",
          groupName: "",
          groupLabel: "",
          questions: data.questions,
        },
      ],
    };
  }

  return null;
};

const removeNullValues = (value: any) => {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined);
  }

  return value;
};

const hasInitialValue = (value: any) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== null && value !== undefined;
};

const hasAnswerValue = (value: any) => {
  if (Array.isArray(value)) {
    return value.some(
      (item) => item !== null && item !== undefined && item !== ""
    );
  }

  return value !== null && value !== undefined && value !== "";
};

const getResponseMessage = (
  value: any,
  language: SurveyLanguage,
  fallback = SURVEY_TEXTS[language].defaultErrorMessage
) => {
  const localizedValue = getLocalizedText(value, language);

  if (localizedValue) {
    return localizedValue;
  }

  const localizedMessage = getLocalizedText(value?.message, language);

  if (localizedMessage) {
    return localizedMessage;
  }

  const localizedData = getLocalizedText(value?.data, language);

  if (localizedData) {
    return localizedData;
  }

  return fallback;
};

const getAnswers = (answerTypeUUID: string, value: any) => {
  if (!hasAnswerValue(value)) {
    return [];
  }

  if (
    answerTypeUUID === FIELDS_TYPES.DATE ||
    answerTypeUUID === FIELDS_TYPES.DATE_TIME
  ) {
    return [dayjs(value).toISOString().substring(0, 19)];
  }

  if (answerTypeUUID === FIELDS_TYPES.TIME) {
    return [dayjs(value).format(SHOW_TIME_FORMAT)];
  }

  if (
    answerTypeUUID === FIELDS_TYPES.INPUT_NUMBER ||
    answerTypeUUID === FIELDS_TYPES.SELECT_NUMBER
  ) {
    return [+value];
  }

  if (answerTypeUUID === FIELDS_TYPES.SELECT_NUMBERS) {
    return Array.isArray(value) ? value.map(Number) : [+value];
  }

  return Array.isArray(value) ? value : [value];
};

interface SurveyStateProps {
  type: "success" | "error";
  title: string;
  description?: string;
  action?: React.ReactNode;
}

const SurveyState = ({
  type,
  title,
  description,
  action,
}: SurveyStateProps) => {
  const Icon = type === "success" ? CheckCircleFilled : CloseCircleFilled;

  return (
    <div className={`surveyState surveyState-${type}`}>
      <div className="surveyStatePanel">
        <div className="surveyStateIcon">
          <Icon />
        </div>
        <div className="surveyStateTitle">{title}</div>
        {description && <div className="surveyStateText">{description}</div>}
        {action && <div className="surveyStateActions">{action}</div>}
      </div>
    </div>
  );
};

const Survey: React.FC = () => {
  const query = useUrlSearchParams();
  const queryId = query.get("id");

  const { jsonData, isLoading, handleGetJson, errorData } = useAsyncJsonData();
  const surveyData = useMemo(
    () => getRenderableSurveyData(jsonData),
    [jsonData]
  );

  const [isSuccessfully, setIsSuccessfully] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [language, setLanguage] = useState<SurveyLanguage>(
    DEFAULT_SURVEY_LANGUAGE
  );

  const [api, contextHolder] = notification.useNotification();
  const locale = SURVEY_TEXTS[language];

  useEffect(() => {
    handleGetJson(getSurveyAPI(queryId));
  }, [handleGetJson, queryId]);

  const handleFormSubmit = async (values: any) => {
    setSubmitError("");

    const visibleQuestionFieldNames = surveyData
      ? getVisibleQuestionFieldNames(surveyData.groups, values)
      : null;
    const visibleValues = Object.fromEntries(
      Object.entries(values).filter(
        ([name]) =>
          !visibleQuestionFieldNames || visibleQuestionFieldNames.has(name)
      )
    );

    const questions = Object.keys(visibleValues).map((item) => {
      //Form name includes answerTypeUUID and questionUUID.
      //We have to separate this two values

      const itemInfo = item.split(",");
      const itemInfoObj = {
        answerTypeUUID: itemInfo[0],
        questionUUID: itemInfo[1],
        groupUUID: itemInfo[2],
      };

      const question = {
        questionUUID: itemInfoObj.questionUUID,
        answers: getAnswers(itemInfoObj.answerTypeUUID, visibleValues[item]),
      };

      return itemInfoObj.groupUUID
        ? { ...question, groupUUID: itemInfoObj.groupUUID }
        : question;
    });

    try {
      const { data } = await putSurveyAPI(queryId, { language, questions });

      if (data.status === "success") {
        setIsSuccessfully(true);
      } else {
        const message = getResponseMessage(data.data, language);
        setSubmitError(message);
        api.error({ message });
      }
    } catch (error: any) {
      const message = getResponseMessage(
        error.response?.data,
        language,
        error.message
      );
      setSubmitError(message);
      api.error({ message });
    }
  };

  const initialValues = (surveyData: RenderableSurveyData) => {
    let obj: any = {};

    surveyData.groups.forEach((group) =>
      group.questions.forEach(
        ({ answerType, defaultAnswer, questionUUID, answers }) => {
          const initialValue = removeNullValues(
            surveyData.filled ? answers : defaultAnswer
          );

          if (!hasInitialValue(initialValue)) {
            return;
          }

          const { answerTypeUUID } = answerType;
          const fieldName = getQuestionFieldName(
            answerTypeUUID,
            questionUUID,
            group.groupUUID
          );

          if (
            answerTypeUUID === FIELDS_TYPES.DATE ||
            answerTypeUUID === FIELDS_TYPES.DATE_TIME
          ) {
            obj[fieldName] = dayjs(String(initialValue));
          } else if (answerTypeUUID === FIELDS_TYPES.TIME) {
            obj[fieldName] = dayjs(String(initialValue), SHOW_TIME_FORMAT);
          } else if (answerTypeUUID === FIELDS_TYPES.SELECT_NUMBER) {
            obj[fieldName] = String(initialValue);
          } else if (
            answerTypeUUID === FIELDS_TYPES.SELECT_NUMBERS ||
            answerTypeUUID === FIELDS_TYPES.SELECT_TEXTS
          ) {
            obj[fieldName] = Array.isArray(initialValue)
              ? initialValue.map(String)
              : [String(initialValue)];
          } else {
            obj[fieldName] = initialValue;
          }
        }
      )
    );

    return obj;
  };

  const formInitialValues = useMemo(
    () => (surveyData ? initialValues(surveyData) : undefined),
    [surveyData]
  );
  const pageErrorMessage = getResponseMessage(
    errorData || jsonData?.messageError,
    language,
    locale.defaultErrorMessage
  );
  const successMessage = getResponseMessage(
    jsonData?.messageSuccess,
    language,
    locale.defaultSuccessMessage
  );

  return (
    <div className="container">
      {contextHolder}
      {isLoading ? (
        <div className="spin" aria-label={locale.loading}>
          <Spin size={"large"} />
        </div>
      ) : (
        <div className="formContainer">
          <Logo />
          {isSuccessfully ? (
            <SurveyState
              type="success"
              title={successMessage}
              description={locale.successDescription}
            />
          ) : (
            <div>
              {surveyData ? (
                <SurverForm
                  handleFormSubmit={handleFormSubmit}
                  jsonData={surveyData}
                  initialValues={formInitialValues}
                  isLoading={isLoading}
                  key={surveyData.surveyUUID}
                  language={language}
                  onLanguageChange={setLanguage}
                  submitError={submitError}
                />
              ) : (
                <SurveyState
                  type="error"
                  title={locale.surveyUnavailable}
                  description={pageErrorMessage}
                  action={
                    <Button
                      type="primary"
                      className="surveyStateButton"
                      icon={<ReloadOutlined />}
                      onClick={() => window.location.reload()}
                    >
                      {locale.refresh}
                    </Button>
                  }
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Survey;
